// src/hooks/useFocusTrap.js
//
// P2-4 (PROFIL_DERIN_ANALIZ_V2 §4.6) — Modal focus trap.
//
// Profil modallarında (TakipListesi, ProfileShareQRModal, CreateSheet,
// ProfileActionsSheetMobile) klavye odağı modal dışına taşabiliyordu ve
// çoğunda Escape kapatması yoktu. Bu hook:
//   - Açılışta ilk odaklanabilir elemana focus() verir.
//   - Tab / Shift+Tab odağı modal içinde döndürür.
//   - Escape onClose'u çağırır.
//   - Kapanışta odağı önceki elemana iade eder.
//
// Kullanım:
//   const ref = useRef(null);
//   const katman = useFocusTrap({ open, onClose, containerRef: ref });
//   <div ref={ref} {...katman} aria-label="…">…</div>
//
// ⚠️ `role`/`aria-modal` ELLE YAZILMAZ — kanca döndürür (aşağıdaki gerekçe).

import type { RefObject } from "react";
import { useEffect } from "react";
import {
  tuzagiKaydet,
  tuzagiCikar,
  enUsttekiTuzakMi,
} from "./odakTuzagiYigini";

/**
 * ── BEYAN, HAPİSLE AYNI KAYNAKTAN ÇIKAR (EMİR #98 Dalga-2 · (b)) ─────────
 *
 * ⚠️ NEDEN: `aria-modal="true"` ile odak hapsi AYRI AYRI iddia ediliyordu ve
 * ölçüm ikisinin AYRIŞTIĞINI gösterdi: sekiz katman (`cs-sheet`, `pqr-modal`,
 * `pas-sheet`, `gis-sheet`, `sjs-sheet`, `mci-sheet`, `kp-follow-overlay`,
 * `modal-global`) hiçbir şey açılmadan «arka planı yok say» diyordu. Hepsi
 * hep-mounted; yalnız CSS `open` sınıfıyla ekrana kayıyorlar, `aria-modal`
 * ise KOŞULSUZ yazılmış. Hapis `open`e bağlıydı, beyan değil.
 *
 * ⚠️ KİLİTLEDİĞİM DEĞİŞMEZİN KAPSAMI EKSİKTİ: `aria-modal ⟺ useFocusTrap`
 * testi DOSYA düzeyinde bakıyordu; gerçek değişmez DURUM düzeyinde. Kural
 * doğruydu, kapsamı dardı — kilit bu yüzden sekiz katmanı sessizce geçirdi.
 *
 * ÇÖZÜM YASAK DEĞİL ÜRETİM: nitelikleri kanca döndürür. Böylece "kapalıyken
 * `aria-modal` diyor" hâli bir kuralla yasaklanmış olmaz, YAPISAL OLARAK
 * İMKÂNSIZ olur — beyan `open`in fonksiyonudur. (Türet, kopyalama.)
 *
 * ⚠️ `data-odak-hapsi` BİLEREK BU NESNEDE DEĞİL. O, `open`in değil
 * BAĞLANIŞIN kanıtı: yalnız `open === true` VE `containerRef` gerçek bir DOM
 * düğümüne çözülmüşken, effect içinde basılır. Döndürülen nitelik yapılsaydı
 * `open` true olur olmaz görünür, ref hiç bağlanmasa bile — yani kancanın
 * yakalamak için var olduğu üç sessiz kusuru (yanlış ref · portal'da kopan
 * ref · açıkken open=false) GÖRÜNMEZ KILARDI. İkisi ayrı şey ölçüyor:
 *   `aria-modal`        → vaat verildi mi (open'ın fonksiyonu)
 *   `data-odak-hapsi`   → vaat ÖDENDİ mi (bağlanışın fonksiyonu)
 * Dedektörün hükmü ikisinin FARKINDA: görünür her `aria-modal`, işaretli
 * kökün kendisi ya da torunu olmalı (`kapsamaDisi === 0`).
 */
const KATMAN_ACIK = Object.freeze({ role: "dialog", "aria-modal": "true" as const });
const KATMAN_KAPALI = Object.freeze({});
const UYARI_ACIK = Object.freeze({ role: "alertdialog", "aria-modal": "true" as const });

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * ── SÖZLEŞME: `data-odak-hapsi` BİR TEST KANCASI DEĞİLDİR ────────────────
 * Bu öznitelik, erişilebilirlik sözleşmesinin GÖRÜNÜR HÂLİDİR: "bu katmanda
 * odak hapsi ARMED". `aria-modal="true"` vaadinin karşılandığını gösteren tek
 * makine-okunur kanıt odur.
 *
 * ⚠️ TEMİZLENECEK BİR ARTIK DEĞİL. "Test attribute'larını sıyır" diyen bir
 * build eklentisi bunu silerse dedektör SESSİZCE körleşir — kaybolan şey bir
 * hata değil, bir KANIT olduğu için kimse fark etmez.
 * `scripts/hosting_yayin_dogrula.js` üretim paketinde varlığını doğruluyor;
 * yoksa deploy DOĞRULANMAMIŞ sayılır.
 */
/**
 * Girdi — 72 çağrı yeri var; `containerRef` kimi yerde
 * `useRef<HTMLDivElement|null>`, kimi yerde çıplak `useRef(null)` →
 * salt-okunur `current` sözleşmesi (`RefObject`) ikisini de kabul eder.
 */
export type UseFocusTrapOptions = {
  open?: boolean;
  // `| null`: gövde `onClose?.()` ile çağırıyor — opsiyonel çağrı `null` ve
  // `undefined`ı AYNI şekilde karşılıyor. Kancayı kullanan ~65 modalın bir
  // kısmı prop'unu `(() => void) | null` ilan ediyor (ör. ProfilePreviewSheet);
  // yalnız `undefined` istemek gövdenin yapmadığı bir daraltmaydı.
  onClose?: (() => void) | null;
  containerRef?: RefObject<HTMLElement | null> | null;
  /** true → uyarı rolü (`alertdialog`). */
  uyari?: boolean;
};

export default function useFocusTrap({
  open,
  onClose,
  containerRef,
  uyari = false,
}: UseFocusTrapOptions) {
  useEffect(() => {
    if (!open) return;
    const container = containerRef?.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // ── HAPSİN ATEŞLENDİĞİNİN MAKİNE-OKUNUR İZİ (EMİR #98 Dalga-2) ──────
    // Dalga-2 bu kancayı ~50 katmana yayacak. "Tek satır ekledim" YETMEZ:
    // kanca DOĞRU olup BAĞLANIŞ yanlış olabilir ve bu SESSİZCE olur —
    // `containerRef` yanlış öğeyi gösterir · katman açıkken `open` false
    // kalır · bileşen portal'a taşınıp ref kopar. Üçünde de kod "hapis var"
    // gibi okunur, çalışma-anında hapis YOKTUR.
    // İşaret yalnız İKİ ŞART BİRDEN sağlanınca basılır: `open === true` VE
    // `containerRef` gerçek bir DOM düğümüne çözülmüş. Yani işaretin varlığı
    // "bağlanış tuttu" demektir; yokluğu 50 yerde tek tek aranabilir.
    // Prod'da inert: yalnız bir data-özniteliği, davranışa etkisi yok.
    try { container.setAttribute("data-odak-hapsi", "1"); } catch { /* no-op */ }

    // ── AÇIK TUZAKLAR YIĞINI (P0-1) ────────────────────────────────────
    // Escape `document`e CAPTURE'da bağlı ve `stopPropagation` AYNI düğümdeki
    // dinleyicileri durdurmaz → iki tuzak açıkken tek Escape İKİSİNİ birden
    // ateşliyordu. Gerekçe + ölçüm: `hooks/odakTuzagiYigini.ts`.
    const tuzakKaydi = tuzagiKaydet(container);

    /* ── PORTAL UZANTILARI (opt-in) ───────────────────────────────────────
       ÖLÇÜLEN KUSUR (GÖZ klavye turu, 2026-08-29): `StarRatingV2` 5★ balonunu
       `createPortal(node, document.body)` ile basıyor. Balon MANTIKSAL olarak
       katmana ait, ama DOM'da kabın dışında. Tuzak hem listeyi hem sarma
       kararını yalnız kaba göre verdiği için, kabın son öğesinde `preventDefault`
       + başa sarma yapıyor ve tarayıcı portal'a HİÇ ulaşamıyordu.
       Sonuç: 5★ — ürünün TEK kalite sinyali — modal içinde fareyle
       yapılabiliyor, KLAVYEYLE YAPILAMIYORDU (WCAG 2.1.1).

       Çare OPT-IN: yalnız `data-odak-uzantisi` taşıyan portal kökleri katılır.
       İşareti taşımayan hiçbir portal etkilenmez → bu kancayı kullanan 60+
       katmanın davranışı BİREBİR aynı kalır (kontrol grubuyla ölçüldü:
       `src/portalOdakTuzagiKapisi.test.tsx`). */
    const UZANTI_SECICI = "[data-odak-uzantisi]";
    const uzantiKokleri = (): HTMLElement[] =>
      (Array.from(document.querySelectorAll(UZANTI_SECICI)) as HTMLElement[])
        .filter((k) => !container.contains(k));   // kapta olan zaten sayılıyor

    /** Odak MANTIKSAL olarak bu katmanda mı? (kap + işaretli portal'lar) */
    const kapsamda = (el: Element | null): boolean =>
      !!el && (container.contains(el) || uzantiKokleri().some((k) => k.contains(el)));

    // Not: offsetParent position:fixed sheet'lerde null döner;
    // görünürlük testi getClientRects ile yapılır.
    //
    // ── `visibility:hidden` / `inert` ATASI = LİSTEDE YOK (GÖZ klavye turu,
    // 2026-09-04) ──────────────────────────────────────────────────────────
    // Kapalı bir alt katman (`storyDk-menulayer`, `cs-sheet`…) `visibility:
    // hidden` ile odak sırasından ÇIKAR — tarayıcı onu Tab'da atlar. Ama
    // `getClientRects` bu öğeler için hâlâ kutu döndürür (yerleşim var,
    // boyama yok). Liste tarayıcının gezmediği bir öğeyi `last` sayarsa,
    // gerçek son görünür öğede tuzak ARAYA GİRMEZ ve tarayıcı odağı kabın
    // DIŞINA taşır (bir adımlık kaçış → sonraki Tab'da geri çekilir).
    // Yani tuzağın listesi, tarayıcının listesiyle AYNI olmalı. Ölçütler
    // tarayıcının kendi kuralı: `visibility:hidden` odaklanamaz, `inert`
    // altı odaklanamaz. `opacity:0` BİLEREK dışarıda — o odaklanabilir kalır
    // (kusurun ta kendisi) ve listeden atmak onu gizlerdi, düzeltmezdi.
    const gorunur = (el: HTMLElement) => {
      if (el.getClientRects().length === 0) return false;
      if (el.closest("[inert]")) return false;
      try {
        return getComputedStyle(el).visibility !== "hidden";
      } catch {
        return true; // ÖLÇEMEDİM → eski davranış (öğe listede kalır)
      }
    };
    const getFocusable = (): HTMLElement[] => {
      const icerdekiler = Array.from(
        container.querySelectorAll(FOCUSABLE_SELECTOR),
      ) as HTMLElement[];
      // Uzantılar SONA eklenir: portal `document.body`nin sonunda durduğu için
      // tarayıcının yerel sekme sırası da onları kaptan SONRA gezer. Liste o
      // sırayı taklit etmezse `last` yanlış öğe olur ve sarma erken tetiklenir.
      const uzantidakiler: HTMLElement[] = [];
      uzantiKokleri().forEach((kok) => {
        if (kok.matches(FOCUSABLE_SELECTOR)) uzantidakiler.push(kok);
        uzantidakiler.push(
          ...(Array.from(kok.querySelectorAll(FOCUSABLE_SELECTOR)) as HTMLElement[]),
        );
      });
      return [...icerdekiler, ...uzantidakiler].filter(gorunur);
    };

    // Açılış animasyonu/render sonrası ilk odak.
    const focusTimer = setTimeout(() => {
      const list = getFocusable();
      const target = list[0] || container;
      // iOS: ilk odaklanabilir bir INPUT/TEXTAREA ise programatik focus()
      // sanal klavyeyi İSTENMEDEN açar → iOS'ta input'a oto-focus verme
      // (kullanıcı dokununca açılır). Diğer elemanlar (buton) sorunsuz.
      const isIos = /iPad|iPhone|iPod/.test(
        typeof navigator !== "undefined" ? navigator.userAgent : "",
      );
      const tag = (target as HTMLElement)?.tagName;
      if (isIos && (tag === "INPUT" || tag === "TEXTAREA")) return;
      try { target.focus?.(); } catch { /* no-op */ }
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // ── 🐞 SENTETİK ESCAPE'İ YOK SAY (ölçülmüş kullanıcı-görünür kusur) ──
        // `public/index.html`deki "GLOBAL POPUP MANAGER" her `pointerdown`da
        // fazla açık menüleri budar ve buduğu her elemana `bubbles:true` bir
        // SENTETİK Escape yollar (`softClose`), olayı `_fromPrune` ile
        // işaretleyerek. Manager KENDİ dinleyicisinde bu bayrağı zaten kontrol
        // ediyor (`if (e.key === "Escape" && !e._fromPrune)`) — ama bu kanca
        // etmiyordu, dolayısıyla budama Escape'ini GERÇEK kullanıcı Escape'i
        // sanıp `onClose()` çağırıyordu.
        //
        // ZİNCİR (uçtan uca ölçüldü): `ProfileActionsSheetMobile` üç
        // `div.pas-list role="menu"` render ediyor ve sheet KAPALIYKEN bile
        // DOM'da duruyor (kapanış yalnız `transform: translateY` sınıfı).
        // Manager'ın görünürlük ölçütü `width>0 && height>0` olduğu için
        // ekran dışına ötelenmiş bu üç düğüm "üç AÇIK menü" sayılıyor;
        // jenerik `div[role="menu"]` seçicisi onları yakalıyor; budayıcı
        // ikisine sentetik Escape atıyor → mobil profilde HERHANGİ bir
        // dokunuş modalı kapatıyordu (kart çevirme, "Vitrine ekle", paylaş)
        // ve takipçi/takip listesi mobilde HİÇ açılmıyordu. Masaüstünde
        // `.pas-list` yok → orada sorun görünmüyordu.
        //
        // Bu guard neden güvenli: `_fromPrune` YALNIZ manager'ın kendi
        // ürettiği olaylarda var. Gerçek klavye Escape'i (isTrusted) bu
        // bayrağı ASLA taşımaz → kullanıcının Escape'i aynen çalışır.
        // 🧭 Aynı sözleşmeyi iki dinleyiciden yalnız biri uygularsa, sözleşme
        //    yok demektir — okumayan taraf onu sessizce ihlal eder.
        if ((e as KeyboardEvent & { _fromPrune?: boolean })._fromPrune) return;
        // ── ESCAPE EN ÜSTTEKİ KATMANINDIR ───────────────────────────────
        // Alttaki katman Escape'i YUTMAZ: iç içe tuzakta yalnız en üstteki
        // kapanır. Tek tuzak varken bu kontrol daima `true` döner → mevcut
        // 60+ katmanın davranışı birebir korunur. (Tab dalı BİLEREK
        // kapsanmadı: bu tur Escape'in ölçülmüş veri kaybını kapatıyor;
        // Tab'ın iç içe sarma sırası ayrı bir ölçüm işidir.)
        if (!enUsttekiTuzakMi(tuzakKaydi)) return;
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const list = getFocusable();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      // `container.contains` DEĞİL `kapsamda`: portal'daki bir yıldıza inen
      // odak, kabın dışında olduğu için "kaçmış" sayılıp anında geri
      // çekiliyordu. Listeyi genişletip bu testi genişletmemek, kusuru
      // yarım düzeltirdi (yıldızlara varılır ama aralarında gezilemezdi).
      if (e.shiftKey) {
        if (active === first || !kapsamda(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !kapsamda(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    // capture: altta kalan sayfanın kendi keydown handler'larından önce.
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown, true);
      // Yığın kapanışta ÇÖZÜLÜR: üstteki gidince alttaki yeniden en üsttedir.
      // (Çözülmezse düzeltme, Escape'i kalıcı öldüren bir "yeşil" olurdu.)
      tuzagiCikar(tuzakKaydi);
      // İşaret hapisle BİRLİKTE kalkar: kapanmış bir katmanda kalan iz,
      // "hapis var" diye yanlış okunurdu (bayat izin sahte-yeşili).
      try { container.removeAttribute("data-odak-hapsi"); } catch { /* no-op */ }
      try { previouslyFocused?.focus?.(); } catch { /* no-op */ }
    };
  }, [open, onClose, containerRef]);

  // Donmuş sabitler döner: her render'da yeni nesne üretilseydi katman kökü
  // gereksiz yere yeniden render alırdı (kanca ~60 yere yayılacak).
  // Erişilebilir AD burada DEĞİL: `aria-label`/`aria-labelledby` İÇERİKTİR,
  // görünür başlıktan türetilir ve çağrı yerinde kalır (Dalga-1 kuralı).
  if (!open) return KATMAN_KAPALI;
  return uyari ? UYARI_ACIK : KATMAN_ACIK;
}
